import { Control, Controller } from "react-hook-form";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { MultiCombobox, ComboboxOption } from "@/components/shadcn/combobox";

export type OptionType = ComboboxOption;

interface IMultiSelect {
  placeholder?: string | null;
  options: OptionType[];
  maxOptions?: number;
  label: string;
  onChange: (value: OptionType[]) => any;
  value: OptionType[];
  tooltip?: string;
  helperText?: string;
  isDisabled?: boolean;
  name?: string;
  selectAll?: boolean;
}

export const MultiSelect = ({
  placeholder,
  options,
  maxOptions,
  label,
  onChange,
  value,
  helperText,
  isDisabled,
  tooltip,
  name,
  selectAll,
}: IMultiSelect) => {
  const handleOnChange = (selectedValues: string[]) => {
    const selectedOptions = selectedValues
      .map((val) => options.find((option) => option.value === val)!)
      .filter(Boolean);
    onChange(selectedOptions);
  };

  const currentValues = value.map((v) => v.value);

  return (
    <div className="w-full">
      {label && <NoxusFormLabel label={label} tooltip={tooltip} name={name} />}
      <MultiCombobox
        options={options}
        value={currentValues}
        placeholder={placeholder || undefined}
        onValueChange={handleOnChange}
        disabled={isDisabled}
        maxOptions={maxOptions}
        multiple={true}
        selectAll={selectAll}
      />
      {helperText && (
        <div className="mt-1.5 text-xs font-regular text-muted-foreground">
          {helperText}
        </div>
      )}
    </div>
  );
};

interface IMultiSelectField {
  name: string;
  required?: boolean;
  hasStar?: boolean;
  control: Control<any>;
  placeholder?: string | null;
  options: OptionType[];
  maxOptions?: number;
  minOptions?: number;
  label: string;
  helperText?: string;
  defaultValue?: OptionType[];
  tooltip?: string;
  isDisabled?: boolean;
  selectAll?: boolean;
}
const MultiSelectField = ({
  name,
  control,
  options,
  tooltip,
  placeholder,
  label,
  helperText,
  maxOptions,
  minOptions,
  defaultValue,
  isDisabled,
  selectAll,
}: IMultiSelectField) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        // Normalize value to options — handles raw scalars (string or numeric
        // option values, e.g. IntEnum-backed options) and legacy OptionType[]
        const valueAsOptions: OptionType[] = (value || []).map(
          (v: string | number | OptionType) =>
            typeof v === "string" || typeof v === "number"
              ? options.find((o) => String(o.value) === String(v)) || {
                  value: String(v),
                  label: String(v),
                }
              : v
        );

        const handleChange = (selectedOptions: OptionType[]) => {
          // Enforce minimum selection count
          if (minOptions && selectedOptions.length < minOptions) {
            return;
          }
          // Always persist string values so the backend receives list[str]
          onChange(selectedOptions.map((o) => o.value));
        };

        return (
          <MultiSelect
            placeholder={placeholder}
            options={options}
            label={label}
            onChange={handleChange}
            value={valueAsOptions}
            tooltip={tooltip}
            helperText={helperText}
            maxOptions={maxOptions}
            name={name}
            isDisabled={isDisabled}
            selectAll={selectAll}
          />
        );
      }}
    />
  );
};

export default MultiSelectField;
